// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { bindAircraftFleetReorder } from "./aircraft-fleet-reorder.js";
import { downloadUploadRawFile } from "./upload-raw-download.js";
import { downloadGraphImage } from "./graph-image-download.js";
import { dismissUploadOnHover, clearUploadsNewOnLeave } from "./new-item-marks.js";
import { normalizeFaaNNumber } from "../core/faa-aircraft-registry.js";
import { showPlanLimitDialog } from "./confirm-dialogs.js";

// One-shot DOM event wiring for the app shell:
// - Wires the toolbar/dialog buttons (upload, remove, report, AI work
//   notice, normalized-CSV download, phase filters, explorer height).
// - Wires the upload, aircraft edit, and review-settings dialogs end to
//   end (open/close/save/cancel, monitor-select sync, photo upload).
// - Wires the aircraft fleet list (click/edit/delete/keyboard/drag-drop).
// - Wires the aircraft flights panel (search, date filter, trend toggle,
//   bulk actions, sort, AI/report/regenerate/open/delete buttons).
// - Wires the navigation tabs (view buttons, hash routing, report tabs,
//   aircraft sub-panel tabs, home-page scroll anchors, empty-state CTA).

export function createNavigationSetup({
  syncExplorerHeightControls,
  syncExplorerFitYControls,
  saveExplorerFitYAxis,
  getExplorerFitYAxis,
  setExplorerFitYAxis,
  syncChartRouteState = () => {},
  appReadOnlyMode,
  setUploadDialogStatus,
  setUploadDialogError,
  readOnlyPreviewMessage,
  readOnlyActionMessage,
  showAppToast,
  syncReadOnlyUi,
  openUploadDialog,
  closeUploadDialog,
  fetchUploadCapacityForAircraft = async () => null,
  databaseAircraftIdForLabel = () => "",
  selectedAircraftWorkspace,
  removeSelectedFlightFromList,
  downloadFlightReportPdf,
  hideAiWorkNotice,
  aiSummaryWorkActionRef,
  hideUploadWorkNotice,
  dismissUploadWorkNotice = hideUploadWorkNotice,
  restoreUploadWorkNotice = () => {},
  uploadWorkActionRef,
  downloadNormalizedCsv,
  readNormalizedFlightEnvelope,
  drawFlightChart,
  drawExplorerChart,
  saveExplorerHeightMode,
  scheduleDrawAll,
  cancelTrendAnalysis,
  closeAircraftEditDialog,
  saveAircraftEditDialogForm,
  openAircraftEditDialog,
  removeAircraftFromFleet,
  openAircraftReviewSettingsDialog,
  closeAircraftReviewSettingsDialog,
  resetAircraftReviewSettingsInDialog,
  updateReviewSettingsDialogCount,
  applyReviewSettingsGroupAction,
  saveAircraftReviewSettingsFromDialog,
  syncUploadAircraftMode,
  populateMonitorSelects,
  updateUploadSelectionSummary,
  uploadSelectedFiles,
  initializeContactForm,
  syncContactMonitorFields,
  submitContactUpdateForm,
  openAircraftCreateDialog,
  aircraftEditorField,
  aircraftEditorNameForFieldId,
  setAircraftFormStatus,
  lookupFaaAircraftRegistry,
  applyAircraftRegistryPrefill,
  normalizedAircraftPhotoDataUrl,
  setAircraftFormPhotoDataUrl,
  persistAircraftProfileFromForm,
  resetAircraftForm,
  setAircraftWorkspaceSelection,
  renderAircraftWorkspace,
  aircraftRowDropPlacement,
  clearAircraftFleetDragState,
  reorderAircraftFleetRow,
  visibleFlightEntries,
  setFlightTrendAnalysisIncluded,
  setAircraftFlightsFilter,
  setAircraftFlightsTrendAnalysisIncluded,
  setAircraftFlightsSort,
  openOrGenerateFlightAiSummary,
  openOrGenerateFlightReport,
  regenerateFlightReport,
  reanalyzeFlight,
  reanalyzeAllFlights,
  editFlightRouteInline,
  submitFlightRouteEdit,
  closeFlightRouteEdit,
  editFlightNameInline,
  submitFlightNameEdit,
  closeFlightNameEdit,
  updateSelectedAircraftFlights = async () => {},
  reanalyzeAvailableAircraftFlights = async () => {},
  setAircraftFlightBulkDeleteSelection = () => {},
  setAircraftFlightBulkDeleteSelectionForIds = () => {},
  archiveSelectedAircraftFlights = async () => {},
  markSelectedAircraftFlightsSeen = async () => {},
  activateView,
  loadFlightLog,
  removeFlightEntryFromList,
  aircraftUploadHistoryErrors,
  renderAircraftUploadsPanel,
  reprocessDatabaseUpload,
  reprocessAllDatabaseUploads,
  reprocessSelectedDatabaseUploads = async () => {},
  processShortFlightsForDatabaseUpload,
  deleteDatabaseUpload = async () => {},
  setAircraftUploadBulkDeleteSelection = () => {},
  setAircraftUploadBulkDeleteSelectionForIds = () => {},
  deleteSelectedDatabaseUploads = async () => {},
  syncAircraftLayoutControls,
  applyAircraftEngineMakeSelection,
  applyAircraftEngineModelSelection,
  setAircraftFuelFields,
  syncAircraftTurboLimitFields = () => {},
  updateAircraftEditSummary,
  updateDataSourceLabel,
  setAircraftPanel,
  selectReportPanel,
  preferredUploadAircraft,
  explorerHeightModes,
  getZoomRange,
  setScrubRatio,
  setExplorerSelectedAlertId,
  getExplorerHeightMode,
  setExplorerHeightMode,
  getDraggedAircraftRow,
  setDraggedAircraftRow,
  getAircraftProfiles,
  getFlightData,
  getSelectedFlight,
  getSelectedAircraftPanel = () => "",
}) {
  let aircraftRegistryLookupTimer = null;
  let aircraftRegistryLookupAbort = null;

  function aircraftPlanLock(aircraft) {
    const profile = aircraft ? getAircraftProfiles()?.[aircraft] ?? null : null;
    return profile?.planAccess?.locked === true ? profile.planAccess : null;
  }

  function explainAircraftPlanLock(aircraft) {
    const lock = aircraftPlanLock(aircraft);
    if (!lock) return false;
    showAppToast(
      String(lock.message ?? "This aircraft is retained but locked by the account plan."),
      "info",
    );
    return true;
  }

  function isFlightRowInteractiveTarget(target) {
    return Boolean(target?.closest?.("button, a, input, label, select, textarea, summary, details"));
  }

  // Shared raw-log download dispatch for the Flights and Uploads panels. Both
  // render `[data-upload-download]` buttons that pull the original file through
  // the auth-aware fetch/blob helper (the patched fetch carries the JWT; a
  // top-level navigation would 401). Returns true when it handled the click so
  // the caller stops before its other branches. The button only renders for
  // non-read-only accounts, so no read-only guard is needed here.
  async function handleUploadRawDownloadClick(event) {
    const downloadButton = event.target.closest("[data-upload-download]");
    if (!downloadButton) return false;
    await downloadUploadRawFile({
      uploadId: downloadButton.dataset.uploadDownload,
      fileName: downloadButton.dataset.uploadFilename,
      onError: (message) => showAppToast(message, "error"),
    });
    return true;
  }

  function toggleAircraftRowSelectCell(event, selector, datasetKey, setSelection) {
    const cell = event.target?.closest?.(".aircraft-row-select-cell");
    if (!cell) return false;
    const input = cell.querySelector(selector);
    if (!input || input.disabled) return true;
    if (!event.target.closest?.(selector)) {
      input.checked = !input.checked;
      input.focus?.({ preventScroll: true });
      setSelection(input.dataset?.[datasetKey], input.checked);
    }
    return true;
  }

  function cancelAircraftRegistryPrefill() {
    if (aircraftRegistryLookupTimer) {
      clearTimeout(aircraftRegistryLookupTimer);
      aircraftRegistryLookupTimer = null;
    }
    aircraftRegistryLookupAbort?.abort?.();
    aircraftRegistryLookupAbort = null;
  }

  function scheduleAircraftRegistryPrefill(form = null) {
    if (!form || appReadOnlyMode()) return;
    if (String(form.dataset?.aircraftDatabaseId ?? "").trim() || String(form.dataset?.aircraftOriginalTail ?? "").trim()) return;
    cancelAircraftRegistryPrefill();
    const tailNumber = normalizeFaaNNumber(aircraftEditorField(form, "tailNumber")?.value);
    if (!tailNumber) return;
    if (form.dataset.aircraftRegistryLookupTail === tailNumber) return;
    aircraftRegistryLookupTimer = setTimeout(async () => {
      aircraftRegistryLookupTimer = null;
      if (form.dataset.aircraftRegistryLookupTail === tailNumber) return;
      aircraftRegistryLookupAbort?.abort?.();
      aircraftRegistryLookupAbort = new AbortController();
      form.dataset.aircraftRegistryLookupTail = tailNumber;
      setAircraftFormStatus(`Looking up ${tailNumber} in the FAA registry...`, "info", form);
      try {
        const registryAircraft = await lookupFaaAircraftRegistry(tailNumber, { signal: aircraftRegistryLookupAbort.signal });
        if (normalizeFaaNNumber(aircraftEditorField(form, "tailNumber")?.value) !== tailNumber) return;
        if (!registryAircraft) {
          setAircraftFormStatus(`No assigned FAA registry record found for ${tailNumber}.`, "info", form);
          return;
        }
        const applied = applyAircraftRegistryPrefill(registryAircraft, form);
        setAircraftFormStatus(
          applied.length
            ? `Filled ${tailNumber} from the FAA registry.`
            : `FAA registry match found for ${tailNumber}; existing fields were left unchanged.`,
          "success",
          form,
        );
      } catch (error) {
        if (error?.name === "AbortError") return;
        setAircraftFormStatus(`FAA registry lookup unavailable: ${error.message}`, "error", form);
      }
    }, 500);
  }

  // A member already at their plan's cap should never see the add flow at
  // all: the plan-limit modal opens right at the click, before any picker
  // or form. Resolves true when the flow may proceed; otherwise routes the
  // modal's choice (Archive tab / plans page) and resolves false. Capacity
  // unavailable (offline, shadow account) opens the flow normally - the
  // submit-time gates and the server backstop still apply.
  async function passesPlanLimitGate(scope, aircraftLabel = "") {
    const aircraftId = aircraftLabel ? databaseAircraftIdForLabel(aircraftLabel) : "";
    const capacity = await fetchUploadCapacityForAircraft(aircraftId || null).catch(() => null);
    if (!capacity) return true;
    const blocked = scope === "aircraft"
      ? capacity.aircraftLimited === true && Number(capacity.aircraftRemaining) <= 0
      : capacity.limited === true && Number(capacity.remaining) <= 0;
    if (!blocked) return true;
    const choice = await showPlanLimitDialog({
      scope,
      context: "add",
      limit: scope === "aircraft" ? capacity.aircraftLimit : capacity.limit,
      planLabel: capacity.planLabel,
    });
    if (choice === "plans") window.location.hash = "account/plan";
    return false;
  }

  function setupNavigation() {
    syncExplorerHeightControls();
    syncExplorerFitYControls();
    document.querySelector("#aircraftUploadButton")?.addEventListener("click", async () => {
      if (appReadOnlyMode()) {
        setUploadDialogStatus(readOnlyPreviewMessage());
        showAppToast(readOnlyActionMessage("Uploading logs"), "info");
        syncReadOnlyUi();
        return;
      }
      const aircraft = selectedAircraftWorkspace();
      if (!(await passesPlanLimitGate("flight", aircraft))) return;
      openUploadDialog(aircraft);
    });
    document.querySelector("#action-remove-flight")?.addEventListener("click", () => {
      removeSelectedFlightFromList();
    });
    document.querySelector("#action-report")?.addEventListener("click", () => {
      downloadFlightReportPdf();
    });
    document.querySelector("#aiSummaryWorkNotice")?.addEventListener("click", (event) => {
      if (event.target.closest("[data-ai-work-notice-dismiss]")) {
        hideAiWorkNotice();
        return;
      }
      if (event.target.closest("[data-ai-work-notice-action]")) {
        const action = aiSummaryWorkActionRef();
        hideAiWorkNotice();
        action?.();
      }
    });
    document.querySelector("#uploadWorkNotice")?.addEventListener("click", (event) => {
      if (event.target.closest("[data-upload-work-notice-dismiss]")) {
        // Collapses to the minimized progress bar while uploads/analysis are
        // still running; closes outright once the queue has settled.
        dismissUploadWorkNotice();
        return;
      }
      if (event.target.closest("[data-upload-work-notice-show]")) {
        restoreUploadWorkNotice();
        return;
      }
      const retryButton = event.target.closest("[data-upload-work-notice-retry]");
      if (retryButton) {
        const action = uploadWorkActionRef();
        action?.({ type: "retry", jobId: retryButton.dataset.uploadWorkNoticeRetry });
        return;
      }
      const inspectButton = event.target.closest("[data-upload-work-notice-inspect]");
      if (inspectButton) {
        const action = uploadWorkActionRef();
        action?.({ type: "inspect", jobId: inspectButton.dataset.uploadWorkNoticeInspect });
        return;
      }
      if (event.target.closest("[data-upload-work-notice-action]")) {
        const action = uploadWorkActionRef();
        action?.({ type: "open" });
      }
    });
    document.querySelector("#action-download-normalized-csv")?.addEventListener("click", () => {
      if (appReadOnlyMode()) {
        window.alert(readOnlyActionMessage("Downloading normalized CSV files"));
        syncReadOnlyUi();
        return;
      }
      downloadNormalizedCsv({ selectedFlight: getSelectedFlight(), loadEnvelope: readNormalizedFlightEnvelope })
        .catch((error) => window.alert(`Unable to download the normalized CSV: ${error?.message || "Please try again."}`));
    });
    document.querySelector("#phaseFilter")?.addEventListener("change", () => {
      const zoomRange = getZoomRange();
      setScrubRatio((zoomRange.start + zoomRange.end) / 2);
      drawFlightChart();
    });
    document.querySelector("#explorerPhaseFilter")?.addEventListener("change", () => {
      setExplorerSelectedAlertId(null);
      const zoomRange = getZoomRange();
      setScrubRatio((zoomRange.start + zoomRange.end) / 2);
      drawExplorerChart();
    });
    document.querySelectorAll("[data-explorer-height]").forEach((button) => {
      button.addEventListener("click", () => {
        const nextMode = button.dataset.explorerHeight;
        if (!Object.hasOwn(explorerHeightModes, nextMode) || nextMode === getExplorerHeightMode()) return;
        setExplorerHeightMode(nextMode);
        saveExplorerHeightMode(nextMode);
        syncExplorerHeightControls();
        scheduleDrawAll();
      });
    });
    document.querySelector("#explorerFitYToggle")?.addEventListener("click", () => {
      const next = !getExplorerFitYAxis();
      setExplorerFitYAxis(next);
      saveExplorerFitYAxis(next);
      syncExplorerFitYControls();
      drawExplorerChart();
      syncChartRouteState();
    });
    document.querySelector("#explorerSaveImage")?.addEventListener("click", () => {
      downloadGraphImage({
        canvas: document.querySelector("#explorerChart"),
        selectedFlight: getSelectedFlight(),
        points: getFlightData(),
      });
    });
    document.querySelector("#trendAnalysisCancel")?.addEventListener("click", cancelTrendAnalysis);
    document.querySelector("#uploadDialogClose")?.addEventListener("click", closeUploadDialog);
    document.querySelector("#uploadDialogCancel")?.addEventListener("click", closeUploadDialog);
    document.querySelector("#aircraftEditDialogClose")?.addEventListener("click", closeAircraftEditDialog);
    document.querySelector("#aircraftEditDialogCancel")?.addEventListener("click", closeAircraftEditDialog);
    document.querySelector("#aircraftEditDialogSave")?.addEventListener("click", (event) => {
      event.preventDefault();
      saveAircraftEditDialogForm(event.currentTarget.form);
    });
    document.querySelector("#aircraftEditDialogDelete")?.addEventListener("click", async (event) => {
      const archiveButton = event.currentTarget;
      const form = document.querySelector("#aircraftEditDialogForm");
      const aircraft = archiveButton?.dataset?.aircraftArchive
        || String(form?.dataset?.aircraftOriginalTail ?? "").trim()
        || selectedAircraftWorkspace();
      if (!aircraft) return;
      closeAircraftEditDialog();
      const archived = await removeAircraftFromFleet(aircraft, { triggerButton: archiveButton });
      const aircraftProfiles = getAircraftProfiles();
      if (!archived && aircraftProfiles?.[aircraft]) {
        openAircraftEditDialog({ aircraft });
      }
    });
    document.querySelector("#aircraftReviewSettingsButton")?.addEventListener("click", () => {
      openAircraftReviewSettingsDialog();
    });
    document.querySelector("#aircraftReviewSettingsClose")?.addEventListener("click", closeAircraftReviewSettingsDialog);
    document.querySelector("#aircraftReviewSettingsCancel")?.addEventListener("click", closeAircraftReviewSettingsDialog);
    // Inline (aircraft page Settings tab): every change autosaves after a
    // short debounce. Scheduled only while inline; the modal keeps its
    // explicit Save button.
    let reviewSettingsAutosaveTimer = null;
    const scheduleInlineReviewSettingsSave = () => {
      if (!document.querySelector("#aircraftReviewSettingsDialog")?.classList.contains("is-inline-panel")) return;
      window.clearTimeout(reviewSettingsAutosaveTimer);
      reviewSettingsAutosaveTimer = window.setTimeout(() => saveAircraftReviewSettingsFromDialog(), 600);
    };
    document.querySelector("#aircraftReviewSettingsReset")?.addEventListener("click", () => {
      resetAircraftReviewSettingsInDialog();
      scheduleInlineReviewSettingsSave();
    });
    document.querySelector("#aircraftReviewSettingsForm")?.addEventListener("change", (event) => {
      if (event.target?.matches?.("[data-review-monitor-key]")) updateReviewSettingsDialogCount();
      scheduleInlineReviewSettingsSave();
    });
    document.querySelector("#aircraftReviewSettingsForm")?.addEventListener("click", (event) => {
      const button = event.target?.closest?.("[data-review-group-action]");
      if (!button) return;
      event.preventDefault();
      applyReviewSettingsGroupAction(button);
      scheduleInlineReviewSettingsSave();
    });
    document.querySelector("#aircraftReviewSettingsForm")?.addEventListener("submit", (event) => {
      event.preventDefault();
      saveAircraftReviewSettingsFromDialog();
    });
    document.querySelector("#uploadAircraftSelect")?.addEventListener("change", syncUploadAircraftMode);
    document.querySelector("#uploadChooseFilesButton")?.addEventListener("click", () => {
      if (appReadOnlyMode()) {
        setUploadDialogError(readOnlyActionMessage("Uploading logs"));
        syncReadOnlyUi();
        return;
      }
      const fileInput = document.querySelector("#uploadFileInput");
      const folderInput = document.querySelector("#uploadFolderInput");
      if (folderInput) folderInput.value = "";
      fileInput?.click();
    });
    document.querySelector("#uploadChooseFolderButton")?.addEventListener("click", () => {
      if (appReadOnlyMode()) {
        setUploadDialogError(readOnlyActionMessage("Uploading logs"));
        syncReadOnlyUi();
        return;
      }
      const fileInput = document.querySelector("#uploadFileInput");
      const folderInput = document.querySelector("#uploadFolderInput");
      if (fileInput) fileInput.value = "";
      folderInput?.click();
    });
    document.querySelector("#uploadFileInput")?.addEventListener("change", updateUploadSelectionSummary);
    document.querySelector("#uploadFolderInput")?.addEventListener("change", updateUploadSelectionSummary);
    document.querySelector("#uploadAircraftMonitorMakeInput")?.addEventListener("change", () => {
      populateMonitorSelects(
        document.querySelector("#uploadAircraftMonitorMakeInput"),
        document.querySelector("#uploadAircraftMonitorModelInput"),
        document.querySelector("#uploadAircraftMonitorMakeInput")?.value,
      );
    });
    document.querySelector("#uploadDialogForm")?.addEventListener("submit", uploadSelectedFiles);
    document.querySelector("#aircraftEditDialogForm")?.elements?.monitorMake?.addEventListener("change", (event) => {
      const form = event.currentTarget.form;
      populateMonitorSelects(form?.elements?.monitorMake, form?.elements?.monitorModel, event.currentTarget.value);
      updateAircraftEditSummary(form);
    });
    initializeContactForm();
    document.querySelector("#contactUpdateForm")?.elements?.monitorMake?.addEventListener("change", (event) => {
      const form = event.currentTarget.form;
      populateMonitorSelects(form?.elements?.monitorMake, form?.elements?.monitorModel, event.currentTarget.value, "", { includeOther: true });
      syncContactMonitorFields(form);
    });
    document.querySelector("#contactUpdateForm")?.addEventListener("submit", submitContactUpdateForm);
    document.querySelector("#aircraftSummaryGrid")?.addEventListener("click", (event) => {
      const card = event.target.closest("[data-aircraft-form-field]");
      if (!card) return;
      openAircraftEditDialog({ focusName: aircraftEditorNameForFieldId(card.dataset.aircraftFormField) });
      setAircraftFormStatus(card.dataset.aircraftFormMessage ?? "", "info", document.querySelector("#aircraftEditDialogForm"));
    });
    document.querySelector("#aircraftSummaryGrid")?.addEventListener("keydown", (event) => {
      const card = event.target.closest("[data-aircraft-form-field]");
      if (!card || (event.key !== "Enter" && event.key !== " ")) return;
      event.preventDefault();
      openAircraftEditDialog({ focusName: aircraftEditorNameForFieldId(card.dataset.aircraftFormField) });
      setAircraftFormStatus(card.dataset.aircraftFormMessage ?? "", "info", document.querySelector("#aircraftEditDialogForm"));
    });
    document.querySelector("#aircraftHeaderEditButton")?.addEventListener("click", () => {
      openAircraftEditDialog();
    });
    document.querySelector("#aircraftHeaderAddButton")?.addEventListener("click", async () => {
      if (appReadOnlyMode()) {
        setAircraftFormStatus(readOnlyActionMessage("Adding aircraft"), "info");
        showAppToast(readOnlyActionMessage("Adding aircraft"), "info");
        syncReadOnlyUi();
        return;
      }
      // The selected aircraft anchors the account lookup; a member at the
      // aircraft cap always has at least one aircraft to anchor with.
      if (!(await passesPlanLimitGate("aircraft", selectedAircraftWorkspace()))) return;
      openAircraftCreateDialog();
    });
    document.querySelector("#aircraftPhotoInput")?.addEventListener("change", async (event) => {
      if (appReadOnlyMode()) return;
      const file = event.target.files?.[0] ?? null;
      const aircraft = selectedAircraftWorkspace();
      const aircraftProfiles = getAircraftProfiles();
      const profile = aircraft ? aircraftProfiles?.[aircraft] ?? null : null;
      const form = document.querySelector("#aircraftEditDialogForm");
      resetAircraftForm(profile ? { ...profile, tailNumber: aircraft } : aircraft ? { tailNumber: aircraft } : null, form);
      if (!file) {
        setAircraftFormPhotoDataUrl(null, aircraft);
        return;
      }
      try {
        setAircraftFormStatus("Preparing aircraft photo...", "info");
        const dataUrl = await normalizedAircraftPhotoDataUrl(file);
        setAircraftFormPhotoDataUrl(dataUrl, aircraft);
        await persistAircraftProfileFromForm({
          savingMessage: `Saving photo for ${aircraft || "this aircraft"}...`,
          successMessage: `Aircraft photo saved for ${aircraft || "this aircraft"}.`,
          form,
          requirePropellerType: false,
        });
      } catch (error) {
        event.target.value = "";
        setAircraftFormPhotoDataUrl(null, aircraft);
        setAircraftFormStatus(error.message, "error");
      }
    });
    document.querySelector("#aircraftPhotoClear")?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (appReadOnlyMode()) return;
      const input = document.querySelector("#aircraftPhotoInput");
      const aircraft = selectedAircraftWorkspace();
      const aircraftProfiles = getAircraftProfiles();
      const profile = aircraft ? aircraftProfiles?.[aircraft] ?? null : null;
      const form = document.querySelector("#aircraftEditDialogForm");
      resetAircraftForm(profile ? { ...profile, tailNumber: aircraft } : aircraft ? { tailNumber: aircraft } : null, form);
      if (input) input.value = "";
      setAircraftFormPhotoDataUrl(null, aircraft);
      persistAircraftProfileFromForm({
        savingMessage: `Removing photo for ${aircraft || "this aircraft"}...`,
        successMessage: `Aircraft photo removed for ${aircraft || "this aircraft"}.`,
        form,
        requirePropellerType: false,
      }).catch((error) => {
        setAircraftFormStatus(error.message, "error");
      });
    });
    document.querySelector("#aircraftFleetList")?.addEventListener("click", async (event) => {
      const editButton = event.target.closest("[data-aircraft-edit]");
      if (editButton) {
        const aircraft = editButton.dataset.aircraftEdit;
        if (explainAircraftPlanLock(aircraft)) return;
        if (setAircraftWorkspaceSelection(aircraft, { render: false }) === false) return;
        const aircraftProfiles = getAircraftProfiles();
        const profile = aircraft ? aircraftProfiles?.[aircraft] ?? null : null;
        renderAircraftWorkspace();
        resetAircraftForm(profile ? { ...profile, tailNumber: aircraft } : { tailNumber: aircraft });
        openAircraftEditDialog({ aircraft, focusName: profile ? "make" : "tailNumber" });
        return;
      }
      const archiveButton = event.target.closest("[data-aircraft-archive]");
      if (archiveButton) {
        if (appReadOnlyMode()) {
          setAircraftFormStatus(readOnlyActionMessage("Archiving aircraft"), "info");
          showAppToast(readOnlyActionMessage("Archiving aircraft"), "info");
          syncReadOnlyUi();
          return;
        }
        await removeAircraftFromFleet(archiveButton.dataset.aircraftArchive, { triggerButton: archiveButton });
        return;
      }
      if (event.target.closest(".aircraft-fleet-actions")) return;
      const row = event.target.closest("[data-aircraft-row]");
      if (!row) return;
      if (explainAircraftPlanLock(row.dataset.aircraftRow)) return;
      setAircraftWorkspaceSelection(row.dataset.aircraftRow);
    });
    document.querySelector("#aircraftFleetList")?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (event.target.closest(".aircraft-fleet-actions")) return;
      const row = event.target.closest("[data-aircraft-row]");
      if (!row) return;
      event.preventDefault();
      if (explainAircraftPlanLock(row.dataset.aircraftRow)) return;
      setAircraftWorkspaceSelection(row.dataset.aircraftRow);
    });
    bindAircraftFleetReorder({
      list: document.querySelector("#aircraftFleetList"),
      appReadOnlyMode: () => appReadOnlyMode(),
      aircraftRowDropPlacement: (...args) => aircraftRowDropPlacement(...args),
      clearAircraftFleetDragState: (...args) => clearAircraftFleetDragState(...args),
      reorderAircraftFleetRow: (...args) => reorderAircraftFleetRow(...args),
      getDraggedAircraftRow: () => getDraggedAircraftRow(),
      setDraggedAircraftRow: (value) => { setDraggedAircraftRow(value); },
    });
    // Uploads NEW tag, dismissal 1 of 2: mousing over a tagged row clears it.
    // (Dismissal 2 is leaving the Uploads tab; see syncAircraftPanelState.)
    // mouseover rather than mouseenter so one delegated listener covers rows
    // that the table reconciler swaps in later.
    document.querySelector("#aircraftUploadsBody")?.addEventListener("mouseover", (event) => {
      const cell = event.target.closest?.("[data-upload-new-cell]");
      if (!cell) return;
      const uploadId = cell.closest("[data-upload-row]")?.dataset.uploadRow;
      if (!uploadId) return;
      if (dismissUploadOnHover(selectedAircraftWorkspace(), uploadId)) {
        cell.querySelector(".row-new-tag")?.remove();
        cell.removeAttribute("data-upload-new-cell");
      }
    });
    document.querySelector("#aircraftFlightsBody")?.addEventListener("change", (event) => {
      const selectInput = event.target.closest("[data-aircraft-flight-select]");
      if (selectInput) {
        setAircraftFlightBulkDeleteSelection(selectInput.dataset.aircraftFlightSelect, selectInput.checked);
        return;
      }
      const trendInput = event.target.closest("[data-aircraft-flight-trend]");
      if (!trendInput) return;
      const entry = visibleFlightEntries().find((item) => item.id === trendInput.dataset.aircraftFlightTrend);
      if (!entry) return;
      setFlightTrendAnalysisIncluded(entry, trendInput.checked);
    });
    document.querySelector("#aircraftFlightsSearch")?.addEventListener("input", (event) => {
      setAircraftFlightsFilter({ query: event.target.value || "" });
    });
    document.querySelector("#aircraftFlightsDateFilter")?.addEventListener("change", (event) => {
      setAircraftFlightsFilter({ dateRange: event.target.value || "all" });
    });
    document.querySelector("#aircraftFlightsPanel")?.addEventListener("click", (event) => {
      const bulkTrendButton = event.target.closest("[data-aircraft-flight-trend-bulk]");
      if (bulkTrendButton) {
        setAircraftFlightsTrendAnalysisIncluded(
          selectedAircraftWorkspace(),
          bulkTrendButton.dataset.aircraftFlightTrendBulk === "include",
        );
        return;
      }
      const filterButton = event.target.closest("[data-aircraft-flight-filter]");
      if (!filterButton) return;
      setAircraftFlightsFilter({
        [filterButton.dataset.aircraftFlightFilter]: filterButton.dataset.filterValue || "all",
      });
    });
    document.querySelector("#aircraftFlightsBody")?.addEventListener("click", async (event) => {
      if (await handleUploadRawDownloadClick(event)) return;
      const aiSummaryButton = event.target.closest("[data-aircraft-flight-ai-summary]");
      if (aiSummaryButton) {
        if (appReadOnlyMode() && !aiSummaryButton.classList.contains("has-summary")) {
          window.alert(readOnlyActionMessage("Generating AI summaries"));
          syncReadOnlyUi();
          return;
        }
        const entry = visibleFlightEntries().find((item) => item.id === aiSummaryButton.dataset.aircraftFlightAiSummary);
        if (!entry) return;
        await openOrGenerateFlightAiSummary(entry, aiSummaryButton);
        return;
      }
      const reportButton = event.target.closest("[data-aircraft-flight-report]");
      if (reportButton) {
        if (appReadOnlyMode() && !reportButton.classList.contains("has-report")) {
          window.alert(readOnlyActionMessage("Generating PDF reports"));
          syncReadOnlyUi();
          return;
        }
        const entry = visibleFlightEntries().find((item) => item.id === reportButton.dataset.aircraftFlightReport);
        if (!entry) return;
        await openOrGenerateFlightReport(entry, reportButton);
        return;
      }
      const regenerateReportButton = event.target.closest("[data-aircraft-flight-report-regenerate]");
      if (regenerateReportButton) {
        if (appReadOnlyMode()) {
          window.alert(readOnlyActionMessage("Regenerating PDF reports"));
          syncReadOnlyUi();
          return;
        }
        const entry = visibleFlightEntries().find((item) => item.id === regenerateReportButton.dataset.aircraftFlightReportRegenerate);
        if (!entry) return;
        await regenerateFlightReport(entry, regenerateReportButton);
        return;
      }
      const reanalyzeButton = event.target.closest("[data-aircraft-flight-reanalyze]");
      if (reanalyzeButton) {
        if (appReadOnlyMode()) {
          const action = reanalyzeButton.dataset.aircraftFlightReanalyzeLabel === "Update"
            ? "Updating flights"
            : "Re-analyzing flights";
          window.alert(readOnlyActionMessage(action));
          syncReadOnlyUi();
          return;
        }
        const entry = visibleFlightEntries().find((item) => item.id === reanalyzeButton.dataset.aircraftFlightReanalyze);
        if (!entry) return;
        await reanalyzeFlight(entry, reanalyzeButton);
        return;
      }
      const reprocessButton = event.target.closest("[data-aircraft-flight-reprocess]");
      if (reprocessButton) {
        if (appReadOnlyMode()) {
          window.alert(readOnlyActionMessage("Updating flights"));
          syncReadOnlyUi();
          return;
        }
        const entry = visibleFlightEntries().find((item) => item.id === reprocessButton.dataset.aircraftFlightReprocess);
        if (!entry?.database?.uploadId) return;
        reprocessButton.disabled = true;
        reprocessButton.classList.add("is-generating");
        const label = reprocessButton.querySelector("span");
        if (label) label.textContent = "Queued";
        showAppToast("Updating the flight from its original upload...", "info");
        await reprocessDatabaseUpload(entry.database.uploadId, entry.aircraft);
        return;
      }
      const routeEditButton = event.target.closest("[data-aircraft-flight-route-edit]");
      if (routeEditButton) {
        if (appReadOnlyMode()) {
          showAppToast(readOnlyActionMessage("Editing flight locations"), "info");
          return;
        }
        const entry = visibleFlightEntries().find((item) => item.id === routeEditButton.dataset.aircraftFlightRouteEdit);
        const cell = routeEditButton.closest(".aircraft-flight-route-cell");
        if (entry && cell) editFlightRouteInline(entry, cell);
        return;
      }
      const routeSaveButton = event.target.closest("[data-aircraft-flight-route-save]");
      if (routeSaveButton) {
        const entry = visibleFlightEntries().find((item) => item.id === routeSaveButton.dataset.aircraftFlightRouteSave);
        const cell = routeSaveButton.closest(".aircraft-flight-route-cell");
        if (entry && cell) await submitFlightRouteEdit(entry, cell, routeSaveButton);
        return;
      }
      const routeCancelButton = event.target.closest("[data-aircraft-flight-route-cancel]");
      if (routeCancelButton) {
        const entry = visibleFlightEntries().find((item) => item.id === routeCancelButton.dataset.aircraftFlightRouteCancel);
        if (entry) closeFlightRouteEdit(entry);
        return;
      }
      const nameEditButton = event.target.closest("[data-aircraft-flight-name-edit]");
      if (nameEditButton) {
        if (appReadOnlyMode()) {
          showAppToast(readOnlyActionMessage("Editing flight names"), "info");
          return;
        }
        const entry = visibleFlightEntries().find((item) => item.id === nameEditButton.dataset.aircraftFlightNameEdit);
        // The editor is a modal, so no slot element is needed — the pencil can
        // sit in the Date or the Name cell depending on the column layout.
        if (entry) editFlightNameInline(entry, null);
        return;
      }
      const nameSaveButton = event.target.closest("[data-aircraft-flight-name-save]");
      if (nameSaveButton) {
        const entry = visibleFlightEntries().find((item) => item.id === nameSaveButton.dataset.aircraftFlightNameSave);
        const slot = nameSaveButton.closest("[data-aircraft-flight-name-cell]");
        if (entry && slot) await submitFlightNameEdit(entry, slot, nameSaveButton);
        return;
      }
      const nameCancelButton = event.target.closest("[data-aircraft-flight-name-cancel]");
      if (nameCancelButton) {
        const entry = visibleFlightEntries().find((item) => item.id === nameCancelButton.dataset.aircraftFlightNameCancel);
        if (entry) closeFlightNameEdit(entry);
        return;
      }
      const openButton = event.target.closest("[data-aircraft-flight-open]");
      if (openButton) {
        const entry = visibleFlightEntries().find((item) => item.id === openButton.dataset.aircraftFlightOpen);
        if (!entry) return;
        activateView("dashboard");
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        await loadFlightLog(entry, { activateTrial: true });
        return;
      }
      const flightArchiveButton = event.target.closest("[data-aircraft-flight-archive]");
      if (flightArchiveButton) {
        if (appReadOnlyMode()) {
          window.alert(readOnlyActionMessage("Archiving flights"));
          syncReadOnlyUi();
          return;
        }
        const entry = visibleFlightEntries().find((item) => item.id === flightArchiveButton.dataset.aircraftFlightArchive);
        if (!entry) return;
        await removeFlightEntryFromList(entry, { triggerButton: flightArchiveButton });
        return;
      }
      if (toggleAircraftRowSelectCell(
        event,
        "[data-aircraft-flight-select]",
        "aircraftFlightSelect",
        setAircraftFlightBulkDeleteSelection,
      )) return;
      if (isFlightRowInteractiveTarget(event.target)) return;
      const row = event.target.closest("[data-aircraft-flight-row]");
      const entry = visibleFlightEntries().find((item) => item.id === row?.dataset.aircraftFlightRow);
      if (!entry) return;
      activateView("dashboard");
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      await loadFlightLog(entry, { activateTrial: true });
    });
    document.querySelector("#aircraftUploadsBody")?.addEventListener("change", (event) => {
      const selectInput = event.target.closest("[data-aircraft-upload-select]");
      if (!selectInput) return;
      setAircraftUploadBulkDeleteSelection(selectInput.dataset.aircraftUploadSelect, selectInput.checked);
    });
    document.querySelector("#aircraftUploadsBody")?.addEventListener("click", async (event) => {
      if (await handleUploadRawDownloadClick(event)) return;
      if (toggleAircraftRowSelectCell(
        event,
        "[data-aircraft-upload-select]",
        "aircraftUploadSelect",
        setAircraftUploadBulkDeleteSelection,
      )) return;
      const reprocessButton = event.target.closest("[data-upload-reprocess]");
      const processShortButton = event.target.closest("[data-upload-process-short]");
      const deleteButton = event.target.closest("[data-upload-delete]");
      if (!reprocessButton && !processShortButton && !deleteButton) return;
      if (appReadOnlyMode()) {
        const action = deleteButton ? "Deleting uploads" : processShortButton ? "Processing short flights" : "Reprocessing uploads";
        aircraftUploadHistoryErrors.set(selectedAircraftWorkspace(), readOnlyActionMessage(action));
        renderAircraftUploadsPanel(selectedAircraftWorkspace());
        syncReadOnlyUi();
        return;
      }
      if (deleteButton) {
        await deleteDatabaseUpload(deleteButton.dataset.uploadDelete);
        return;
      }
      if (processShortButton) {
        processShortButton.disabled = true;
        await processShortFlightsForDatabaseUpload(processShortButton.dataset.uploadProcessShort);
        return;
      }
      reprocessButton.disabled = true;
      await reprocessDatabaseUpload(reprocessButton.dataset.uploadReprocess);
    });
    document.querySelector("#aircraftUploadReprocessAll")?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      const aircraft = selectedAircraftWorkspace();
      if (appReadOnlyMode()) {
        aircraftUploadHistoryErrors.set(aircraft, readOnlyActionMessage("Reprocessing uploads"));
        renderAircraftUploadsPanel(aircraft);
        syncReadOnlyUi();
        return;
      }
      button.disabled = true;
      await reprocessSelectedDatabaseUploads(aircraft);
    });
    document.querySelector("#aircraftUploadsSelectAll")?.addEventListener("change", (event) => {
      const ids = Array.from(document.querySelectorAll("#aircraftUploadsBody [data-aircraft-upload-select]:not(:disabled)"))
        .map((input) => input.dataset.aircraftUploadSelect)
        .filter(Boolean);
      setAircraftUploadBulkDeleteSelectionForIds(ids, event.currentTarget.checked);
    });
    document.querySelector("#aircraftUploadsDeleteSelected")?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      const aircraft = selectedAircraftWorkspace();
      if (appReadOnlyMode()) {
        aircraftUploadHistoryErrors.set(aircraft, readOnlyActionMessage("Deleting uploads"));
        renderAircraftUploadsPanel(aircraft);
        syncReadOnlyUi();
        return;
      }
      button.disabled = true;
      await deleteSelectedDatabaseUploads(aircraft);
    });
    document.querySelector("#aircraftFlightsUpdateSelected")?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      const aircraft = selectedAircraftWorkspace();
      if (appReadOnlyMode()) {
        window.alert(readOnlyActionMessage("Updating flights"));
        syncReadOnlyUi();
        return;
      }
      button.disabled = true;
      await updateSelectedAircraftFlights(aircraft);
    });
    // Clear the New tag on the selected flights without having to open each
    // one. Read-only sessions are excluded: this writes per-user review state.
    document.querySelector("#aircraftFlightsMarkSeen")?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      if (appReadOnlyMode()) {
        showAppToast(readOnlyActionMessage("Marking flights as reviewed"), "info");
        syncReadOnlyUi();
        return;
      }
      button.disabled = true;
      try {
        await markSelectedAircraftFlightsSeen();
      } finally {
        button.disabled = false;
      }
    });
    document.querySelector("#aircraftFlightsSelectAll")?.addEventListener("change", (event) => {
      const ids = Array.from(document.querySelectorAll("#aircraftFlightsBody [data-aircraft-flight-select]:not(:disabled)"))
        .map((input) => input.dataset.aircraftFlightSelect)
        .filter(Boolean);
      setAircraftFlightBulkDeleteSelectionForIds(ids, event.currentTarget.checked);
    });
    document.querySelector("#aircraftFlightsArchiveSelected")?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      const aircraft = selectedAircraftWorkspace();
      if (appReadOnlyMode()) {
        showAppToast(readOnlyActionMessage("Archiving flights"), "info");
        syncReadOnlyUi();
        return;
      }
      button.disabled = true;
      await archiveSelectedAircraftFlights(aircraft);
    });
    document.querySelector(".aircraft-flights-table")?.addEventListener("click", (event) => {
      const sortButton = event.target.closest("[data-aircraft-flight-sort]");
      if (!sortButton) return;
      setAircraftFlightsSort(sortButton.dataset.aircraftFlightSort);
    });
    document.querySelector("#uploadDialog")?.addEventListener("cancel", (event) => {
      event.preventDefault();
      closeUploadDialog();
    });
    document.querySelector("#aircraftEditDialog")?.addEventListener("cancel", (event) => {
      event.preventDefault();
      closeAircraftEditDialog();
    });
    document.querySelector("#aircraftReviewSettingsDialog")?.addEventListener("cancel", (event) => {
      event.preventDefault();
      closeAircraftReviewSettingsDialog();
    });
    document.querySelector("#aircraftEditDialogForm")?.addEventListener("input", (event) => {
      const form = event.currentTarget;
      if (event.target?.name === "tailNumber") {
        cancelAircraftRegistryPrefill();
        delete form.dataset.aircraftRegistryLookupTail;
      }
      if (event.target?.name === "engineCount") {
        syncAircraftLayoutControls(form);
        return;
      }
      updateAircraftEditSummary(form);
    });
    document.querySelector("#aircraftEditDialogForm")?.addEventListener("change", (event) => {
      const form = event.currentTarget;
      if (event.target?.name === "tailNumber") {
        scheduleAircraftRegistryPrefill(form);
        updateAircraftEditSummary(form);
        return;
      }
      if (event.target?.name === "engineMake") {
        applyAircraftEngineMakeSelection(form);
        return;
      }
      if (event.target?.name === "engineModel") {
        applyAircraftEngineModelSelection(form);
        return;
      }
      if (event.target?.name === "type" || event.target?.name === "engineLabelMode") {
        syncAircraftLayoutControls(form);
        return;
      }
      if (event.target?.name === "fuelChoice") {
        setAircraftFuelFields(event.target.value, form);
      }
      if (event.target?.name === "isTurbo") {
        syncAircraftTurboLimitFields(form, event.target.checked);
      }
      updateAircraftEditSummary(form);
    });
    document.querySelector("#aircraftEditDialogForm")?.addEventListener("submit", async (event) => {
      event.preventDefault();
      saveAircraftEditDialogForm(event.currentTarget);
    });

    const navItems = document.querySelectorAll("[data-view]");
    navItems.forEach((button) => {
      button.addEventListener("click", (event) => {
        if (button.classList.contains("topbar-user")) return;
        event.preventDefault();
        activateView(button.dataset.view);
        if (button.dataset.view) {
          window.location.hash = button.dataset.view;
        }
      });
    });
    document.querySelectorAll("[data-home-scroll-target]").forEach((button) => {
      button.addEventListener("click", () => {
        const target = document.querySelector(`#${button.dataset.homeScrollTarget}`);
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    // Scoped to the dashboard report tabs: the aircraft page tabs reuse the
    // .tab class for styling but route through data-aircraft-panel below.
    document.querySelectorAll(".report-tabs-row .tab").forEach((tab) => {
      tab.addEventListener("click", () => {
        selectReportPanel(tab.dataset.panel);
      });
    });

    document.querySelectorAll("[data-aircraft-panel]").forEach((tab) => {
      tab.addEventListener("click", async () => {
        try {
          const panel = tab.dataset.aircraftPanel;
          // Uploads NEW tags, dismissal 2 of 2: the user clicking off the
          // Uploads tab counts as having looked. Driven from the click rather
          // than from every panel sync, because the post-upload flow also
          // changes panels and must not clear tags the user never saw.
          if (panel !== "uploads" && getSelectedAircraftPanel() === "uploads") {
            clearUploadsNewOnLeave();
          }
          await setAircraftPanel(panel);
          const nextHash = panel === "profile" ? "aircraft" : panel;
          if (panel === "dashboard") {
            if (!getSelectedFlight()?.id) window.location.hash = "dashboard";
            return;
          }
          if (nextHash && panel !== "trends") {
            window.location.hash = nextHash;
          }
        } catch (error) {
          updateDataSourceLabel(false, error);
        }
      });
    });

    const emptyStateUpload = async () => {
      if (appReadOnlyMode()) {
        setUploadDialogStatus(readOnlyPreviewMessage());
        showAppToast(readOnlyActionMessage("Uploading logs"), "info");
        syncReadOnlyUi();
        return;
      }
      const aircraft = selectedAircraftWorkspace() || preferredUploadAircraft();
      if (!(await passesPlanLimitGate("flight", aircraft))) return;
      // No postSuccessPanel: finishing an upload must not move the user to
      // another tab. The panels refresh in place as flights arrive, so staying
      // put is both less jarring and keeps whatever they were looking at.
      openUploadDialog(aircraft);
    };
    // The dashboard and the Flights tab share the same "No flights yet" empty
    // state; both Upload buttons open the same upload dialog.
    document.querySelector("#dashboardEmptyAircraftUpload")?.addEventListener("click", emptyStateUpload);
    document.querySelector("#flightsEmptyAircraftUpload")?.addEventListener("click", emptyStateUpload);
  }

  return { setupNavigation };
}
